import React, { useState, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import api from '../utils/api';
import WaiverModal from './WaiverModal';
import './Login.css';

const Login = () => {
  const { login, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/login', formData);
      const { token, user } = response.data;
      
      // Only proceed with login and navigation if we have valid data
      if (token && user) {
        // Check if waiver is signed - ONLY for parent users
        if (!user.waiver_signed && user.user_type === 'parent') {
          // Store user data AND token temporarily for waiver submission
          setUserData({ user, token });
          
          // Set token temporarily for API calls (waiver submission will need it)
          localStorage.setItem('token', token);
          
          // Fetch all players for waiver
          try {
            const playersResponse = await api.get('/api/players-with-assessments');
            const players = playersResponse.data.players || [];
            console.log('LOGIN: Fetched players for waiver:', players);
            console.log('LOGIN: Number of players:', players.length);
            setAllPlayers(players);
          } catch (err) {
            console.error('Error fetching players:', err);
            setAllPlayers([]);
          }
          
          setShowWaiverModal(true);
          setLoading(false);
          return;
        }
        
        // If waiver is signed, proceed with normal login
        login(user, token);
        
        // Use a small delay to ensure context updates, then navigate
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 100);
      } else {
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.error || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleWaiverComplete = () => {
    // Complete login after waiver is signed
    if (userData) {
      // Update user object with waiver_signed flag
      const updatedUser = { ...userData.user, waiver_signed: new Date().toISOString() };
      login(updatedUser, userData.token);
      setShowWaiverModal(false);
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
    }
  };

  const handleWaiverLogout = () => {
    // User chose to logout instead of signing waiver
    // Remove token from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setShowWaiverModal(false);
    setUserData(null);
    setAllPlayers([]);
    setError('You must sign the waiver to access your account');
  };

  return (
    <div className="login-container">
      <div className="login-card">
        {/* Header with Cricket Logo */}
        <div className="login-header">
          <div className="cricket-logo">
            <svg width="60" height="60" viewBox="0 0 100 100" className="cricket-icon">
              <circle cx="50" cy="50" r="45" fill="#1e40af" stroke="#ffffff" strokeWidth="3"/>
              <rect x="35" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="55" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="45" y="20" width="6" height="60" fill="#ffffff" rx="3"/>
              <rect x="30" y="75" width="40" height="4" fill="#ffffff" rx="2"/>
              <circle cx="50" cy="35" r="8" fill="#ef4444"/>
            </svg>
          </div>
          <h1 className="academy-title">{process.env.REACT_APP_ACADEMY_NAME || 'MSKICA Cricket Academy'}</h1>
          <p className="academy-subtitle">Professional Training Portal</p>
        </div>

        {/* Login Form */}
        <div className="login-form-section">
          {error && (
            <div className="error-alert">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                <span className="label-icon">📧</span>
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                <span className="label-icon">�</span>
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your password"
                required
              />
            </div>

            <button 
              type="submit" 
              className="login-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Signing In...
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <span className="btn-icon">🏏</span>
                </>
              )}
            </button>
          </form>

          {/* Footer Links */}
          <div className="login-footer">
            <Link to="/register" className="footer-link">
              Don't have an account? <strong>Create one</strong>
            </Link>
            
            <Link to="/forgot-password" className="footer-link forgot-password-link">
              Forgot your password? <strong>Reset it here</strong>
            </Link>
            
            <div className="system-badges">
              <span className="badge">🏏 Performance Tracking</span>
              <span className="badge">👨‍👩‍👧‍👦 Parent Portal</span>
              <span className="badge">👨‍🏫 Coach Dashboard</span>
            </div>
          </div>
        </div>
      </div>

      {/* Waiver Modal */}
      {showWaiverModal && userData && (
        <WaiverModal
          user={userData.user}
          allPlayers={allPlayers}
          onComplete={handleWaiverComplete}
          onLogout={handleWaiverLogout}
        />
      )}
    </div>
  );
};

export default Login;
